import React, { useState } from 'react';
import { X, Download, Send, Edit, FileText, Printer } from 'lucide-react';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate, downloadPDF, sendEmail } from '../../lib/utils';
import { PrintableQuote } from '../print/PrintableQuote';
import { quoteService } from '../../lib/database/quotes';

interface ViewQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onConvertToInvoice: () => void;
  quote: any;
}

export const ViewQuoteModal: React.FC<ViewQuoteModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  onConvertToInvoice,
  quote,
}) => {
  const [showPrintView, setShowPrintView] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!isOpen || !quote) return null;

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // In a real app, this would generate a PDF using a library
      await downloadPDF(`Quote_${quote.quote_number}`, JSON.stringify(quote));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendQuote = async () => {
    setSending(true);
    try {
      // Update quote status to 'sent'
      await quoteService.updateQuote(quote.id, { status: 'sent' });
      
      // Send email notification
      if (quote.client?.email) {
        await sendEmail(
          quote.client.email,
          `Quote ${quote.quote_number} from AI CRM`,
          `Your quote ${quote.quote_number} for ${formatCurrency(Number(quote.total))} is now available. Valid until: ${formatDate(quote.valid_until)}`
        );
      }
      
      // Close modal after sending
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Failed to send quote. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Quote Details</h2>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" icon={Edit} onClick={onEdit}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" icon={Printer} onClick={handlePrint}>
                Print
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                icon={Download} 
                onClick={handleDownloadPDF}
                loading={downloading}
                disabled={downloading}
              >
                PDF
              </Button>
              {quote.status !== 'approved' && quote.status !== 'rejected' && (
                <Button 
                  variant="primary" 
                  size="sm" 
                  icon={Send} 
                  onClick={handleSendQuote}
                  loading={sending}
                  disabled={sending}
                >
                  Send
                </Button>
              )}
              {quote.status === 'approved' && (
                <Button 
                  variant="secondary" 
                  size="sm" 
                  icon={FileText} 
                  onClick={onConvertToInvoice}
                  className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:from-primary-700 hover:to-secondary-700"
                >
                  Convert to Invoice
                </Button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Quote Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quote Information</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">Quote Number:</span>
                    <p className="font-semibold text-gray-900">{quote.quote_number}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Status:</span>
                    <div className="mt-1">
                      <StatusBadge status={quote.status} />
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Created:</span>
                    <p className="text-gray-900">{formatDate(quote.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Valid Until:</span>
                    <p className="text-gray-900">{formatDate(quote.valid_until)}</p>
                    {new Date(quote.valid_until) < new Date() && quote.status !== 'approved' && (
                      <p className="text-sm text-error-600 font-medium">Expired</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">Name:</span>
                    <p className="font-semibold text-gray-900">{quote.client?.name || 'Unknown'}</p>
                  </div>
                  {quote.client?.company && (
                    <div>
                      <span className="text-sm text-gray-600">Company:</span>
                      <p className="text-gray-900">{quote.client.company}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm text-gray-600">Email:</span>
                    <p className="text-gray-900">{quote.client?.email || 'No email'}</p>
                  </div>
                  {quote.client?.phone && (
                    <div>
                      <span className="text-sm text-gray-600">Phone:</span>
                      <p className="text-gray-900">{quote.client.phone}</p>
                    </div>
                  )}
                  {quote.client?.address && (
                    <div>
                      <span className="text-sm text-gray-600">Address:</span>
                      <p className="text-gray-900">{quote.client.address}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quote Items */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quote Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Description</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-900">Qty</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-900">Rate</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-900">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.quote_items?.map((item: any, index: number) => (
                      <tr key={item.id || index} className="border-t border-gray-200">
                        <td className="py-3 px-4 text-gray-900">{item.description}</td>
                        <td className="py-3 px-4 text-right text-gray-900">{Number(item.quantity).toFixed(0)}</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(Number(item.rate))}</td>
                        <td className="py-3 px-4 text-right font-semibold text-gray-900">{formatCurrency(Number(item.amount))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="space-y-2 max-w-sm ml-auto">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(Number(quote.subtotal))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax ({Number(quote.tax_rate)}%):</span>
                  <span className="font-medium">{formatCurrency(Number(quote.tax_amount))}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(Number(quote.total))}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {quote.notes && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes</h3>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{quote.notes}</p>
              </div>
            )}

            {/* Quote to Invoice Conversion Info */}
            {quote.status === 'approved' && (
              <div className="bg-gradient-to-r from-success-50 to-primary-50 border border-success-200 p-6 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-success-800 font-semibold mb-2 flex items-center">
                      <FileText className="w-5 h-5 mr-2" />
                      Ready for Invoice Conversion
                    </h4>
                    <p className="text-success-700 text-sm">
                      This quote has been approved and can be converted to an invoice. 
                      Click "Convert to Invoice" to create an invoice with the same items and client details.
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    icon={FileText}
                    onClick={onConvertToInvoice}
                    className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:from-primary-700 hover:to-secondary-700 shadow-lg"
                  >
                    Convert to Invoice
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Printable Version */}
      <PrintableQuote quote={quote} isVisible={showPrintView} />
    </>
  );
};